import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Translate } from 'react-localize-redux';
import {
    DEV_STATUS_IN_PRODUCTION,
    DEV_STATUS_IN_TESTING,
    IOS_PLATFORM,
    OS_LIST,
    OTHER_PLATFORM,
    WEB_PLATFORM,
} from '../../constants';
import { getPlatformFamilyName, getPublicIdForDisplay, translateText } from '../../utils';
import { PlatformIcon, SiteStatusIcon } from '../controls';
import css from '../AppCard.css';
import {
    appsPermissionsPropType,
    configurationPropType,
    moveTargetPropType,
    platformPropType,
    sitePropType,
    userDataPropType,
} from './propTypes';
import SiteDetails from './SiteDetails';

const SITE_ACTION_ICON = '/static/dashboard/img/dots.svg';

const SiteRow = ({
    appName,
    appId,
    site,
    adminMode,
    isStandardTier,
    expanded,
    onToggle,
    onPatch,
    onRefreshStoreDetails,
    onTestFcmIntegration,
    onJumpToDuplicateSite,
    onRestoreDuplicateSite,
    onSave,
    onCancel,
    isSiteDirty,
    platformNames,
    platforms,
    onDelete,
    onToggleDevelopmentStatus,
    onMoveSite,
    moveTargets,
    readOnly,
    configuration,
    appsPermissions,
    userData,
    translate,
    onTrackAppsEvent,
}) => {
    const platformFamilyName = getPlatformFamilyName(site);
    const concretePlatformName = site?.platform?.name || site?.platform || platformFamilyName;
    const selectedBucketPlatform =
        concretePlatformName === OTHER_PLATFORM || !OS_LIST.includes(String(concretePlatformName))
            ? OTHER_PLATFORM
            : platformFamilyName;
    const siteHeaderPlatformIcon = {
        platform: { name: selectedBucketPlatform },
        is_live_in_store: site?.is_live_in_store,
    };
    const isIosSite = platformFamilyName === IOS_PLATFORM;
    const isWebSite = platformFamilyName === WEB_PLATFORM;
    const defaultDisplayName = site.alias || site.longname || site.url || getPublicIdForDisplay(site.public_id);
    const webDisplayName = site.alias || site.url || getPublicIdForDisplay(site.public_id) || site.longname;
    const publicIdDisplayName = getPublicIdForDisplay(site.public_id, '');
    const collapsedPrimaryName =
        site.alias ||
        (site.id && isWebSite ? webDisplayName : null) ||
        (site.id && (isIosSite || selectedBucketPlatform === OTHER_PLATFORM)
            ? publicIdDisplayName || defaultDisplayName
            : defaultDisplayName);
    const collapsedSecondaryName = isIosSite && site.longname ? site.longname : null;
    const showCollapsedOtherPlatform = !expanded && site.id && selectedBucketPlatform === OTHER_PLATFORM;
    const [showActionsMenu, setShowActionsMenu] = useState(false);
    const [showMoveTargets, setShowMoveTargets] = useState(false);
    const [moveTargetSearch, setMoveTargetSearch] = useState('');
    const actionsMenuRef = useRef(null);
    const nextStatus =
        site.development_status === DEV_STATUS_IN_PRODUCTION ? DEV_STATUS_IN_TESTING : DEV_STATUS_IN_PRODUCTION;
    const statusLabel =
        nextStatus === DEV_STATUS_IN_PRODUCTION
            ? translateText(translate, 'STATIC.PAGES.APPS_CONFIGURATION.MOVE_TO_LIVE')
            : translateText(translate, 'STATIC.PAGES.APPS_CONFIGURATION.MOVE_TO_TEST');
    const visibleMoveTargets = moveTargets.filter(targetApp =>
        (targetApp.name || '').toLowerCase().includes(moveTargetSearch.trim().toLowerCase())
    );

    useEffect(() => {
        if (!showActionsMenu) {
            return undefined;
        }

        const closeMenuOnOutsideClick = event => {
            if (actionsMenuRef.current?.contains(event.target)) {
                return;
            }

            setShowActionsMenu(false);
            setShowMoveTargets(false);
        };

        document.addEventListener('mousedown', closeMenuOnOutsideClick);

        return () => document.removeEventListener('mousedown', closeMenuOnOutsideClick);
    }, [showActionsMenu]);

    return (
        <div className={css.siteBlock}>
            <div
                className={classNames(css.siteRow, { [css.siteRowExpanded]: expanded })}
                onClick={onToggle}
                role="button"
                tabIndex={0}
                onKeyDown={event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        onToggle();
                    }
                }}
            >
                <div className={css.sitePlatform}>
                    <PlatformIcon site={siteHeaderPlatformIcon} />
                    <span className={css.siteDivider} />
                    <span className={classNames(css.caret, { [css.caretOpen]: expanded })}>▸</span>
                </div>
                <div className={css.siteMain}>
                    <div className={css.siteName} title={collapsedPrimaryName}>
                        {collapsedPrimaryName}
                        {adminMode && site.id ? <span className={css.adminId}>Appsite ID: {site.id}</span> : null}
                    </div>
                    {expanded ? null : collapsedSecondaryName ? (
                        <div className={css.siteMeta}>{collapsedSecondaryName}</div>
                    ) : null}
                </div>
                <div className={css.sdkStatus}>
                    <SiteStatusIcon integrated={site.sdk_integrated} />
                    {site.sdk_integrated ? (
                        <Translate id="STATIC.PAGES.APPS_CONFIGURATION.SDK_INTEGRATED" />
                    ) : (
                        <Translate id="STATIC.PAGES.APPS_CONFIGURATION.SDK_NOT_INTEGRATED" />
                    )}
                    {showCollapsedOtherPlatform ? (
                        <>
                            <span className={css.siteDivider} />
                            <span>
                                Platform : {site.platform?.display_name || site.platform?.name || site.platform}
                            </span>
                        </>
                    ) : null}
                </div>
                {!readOnly && site.id ? (
                    <div className={css.actionsMenuWrap} ref={actionsMenuRef}>
                        <button
                            type="button"
                            className={css.kebabButton}
                            title={translateText(translate, 'STATIC.PAGES.APPS_CONFIGURATION.MORE_ACTIONS')}
                            aria-label={translateText(translate, 'STATIC.PAGES.APPS_CONFIGURATION.MORE_ACTIONS')}
                            onClick={event => {
                                event.stopPropagation();
                                setShowActionsMenu(prev => !prev);
                            }}
                        >
                            <img src={SITE_ACTION_ICON} alt="" />
                        </button>
                        {showActionsMenu ? (
                            <div className={css.actionsMenu} onClick={event => event.stopPropagation()}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowActionsMenu(false);
                                        onDelete();
                                    }}
                                >
                                    <Translate id="STATIC.PAGES.APPS_CONFIGURATION.DELETE_SITE" />
                                </button>
                                {!isStandardTier ? (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowActionsMenu(false);
                                            onToggleDevelopmentStatus(nextStatus);
                                        }}
                                    >
                                        {statusLabel}
                                    </button>
                                ) : null}
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowMoveTargets(prev => !prev);
                                        setMoveTargetSearch('');
                                    }}
                                >
                                    <Translate id="STATIC.PAGES.APPS_CONFIGURATION.MOVE_TO_ANOTHER_APP" />
                                </button>
                                {showMoveTargets ? (
                                    <div className={css.moveTargets}>
                                        <input
                                            value={moveTargetSearch}
                                            placeholder={translateText(
                                                translate,
                                                'STATIC.PAGES.APPS_CONFIGURATION.SEARCH_FOR_APP'
                                            )}
                                            onChange={event => setMoveTargetSearch(event.target.value)}
                                        />
                                        {visibleMoveTargets.length ? (
                                            visibleMoveTargets.map(targetApp => (
                                                <button
                                                    key={targetApp.id}
                                                    type="button"
                                                    title={targetApp.name}
                                                    onClick={() => {
                                                        setShowActionsMenu(false);
                                                        onMoveSite(targetApp.id);
                                                    }}
                                                >
                                                    {targetApp.name}
                                                </button>
                                            ))
                                        ) : (
                                            <span>
                                                <Translate id="STATIC.PAGES.APPS_CONFIGURATION.NO_RESULTS" />
                                            </span>
                                        )}
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </div>
            {expanded ? (
                <SiteDetails
                    appId={appId}
                    appName={appName}
                    site={site}
                    adminMode={adminMode}
                    configuration={configuration}
                    appsPermissions={appsPermissions}
                    userData={userData}
                    readOnly={readOnly}
                    onSitePatch={onPatch}
                    onRefreshStoreDetails={onRefreshStoreDetails}
                    onTestFcmIntegration={onTestFcmIntegration}
                    onJumpToDuplicateSite={onJumpToDuplicateSite}
                    onRestoreDuplicateSite={onRestoreDuplicateSite}
                    onSave={onSave}
                    onCancel={onCancel}
                    siteDirty={isSiteDirty(site)}
                    platformNames={platformNames}
                    platforms={platforms}
                    translate={translate}
                    onTrackAppsEvent={onTrackAppsEvent}
                />
            ) : null}
        </div>
    );
};

SiteRow.propTypes = {
    adminMode: PropTypes.bool.isRequired,
    appId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    appName: PropTypes.string.isRequired,
    appsPermissions: appsPermissionsPropType.isRequired,
    configuration: configurationPropType.isRequired,
    expanded: PropTypes.bool.isRequired,
    isSiteDirty: PropTypes.func.isRequired,
    isStandardTier: PropTypes.bool.isRequired,
    moveTargets: PropTypes.arrayOf(moveTargetPropType).isRequired,
    onCancel: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    onJumpToDuplicateSite: PropTypes.func.isRequired,
    onMoveSite: PropTypes.func.isRequired,
    onPatch: PropTypes.func.isRequired,
    onRefreshStoreDetails: PropTypes.func.isRequired,
    onRestoreDuplicateSite: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    onTestFcmIntegration: PropTypes.func.isRequired,
    onToggle: PropTypes.func.isRequired,
    onToggleDevelopmentStatus: PropTypes.func.isRequired,
    onTrackAppsEvent: PropTypes.func.isRequired,
    platformNames: PropTypes.arrayOf(PropTypes.string).isRequired,
    platforms: PropTypes.arrayOf(platformPropType).isRequired,
    readOnly: PropTypes.bool.isRequired,
    site: sitePropType.isRequired,
    translate: PropTypes.func.isRequired,
    userData: userDataPropType.isRequired,
};

export default SiteRow;
